
import { Component, Vue } from "vue-property-decorator";
import moment from "moment";
import axios from "axios";

@Component({
    components: {
        Paginator: () => import("@/components/Paginator.vue"),
        SortColumn: () => import("@/components/SortColumn.vue"),
        Modal: () => import("@/components/Modal.vue"),
    },
    filters: {
        date(value) {
            return value ? moment(value, "YYYY-MM-DD").format("DD/MM/YYYY") : "";
        }
    }
})
export default class BatchList extends Vue {
    [x: string]: any;

    filter: any = {
        code: "",
        requestNumber: "",
        claimNumber: "",
        stateId: ""
    };

    loading: boolean = true;
    batches: any[] = [];
    batchesStates: any[] = [];
    totalRows: number = 0;
    limit: number = 10;
    currentSort: Record<string, string> = {
        col: "batches.code",
        dir: "asc"
    };
    pendingBatchId: number | null = null;
    pendingStateChange: { batchId: number; targetState: string; label: string; sourceStateId: number } | null = null;
    bordereauxOptions: { sendEmail: boolean; emailTo: string } = { sendEmail: false, emailTo: "" };

    IN_PROGRESS_STATE_ID: number = 6;
    ERROR_STATE_ID: number = 7;
    PENDING_STATE_ID: number = 1;
    ACCEPTED_STATE_ID: number = 2;
    PROCESSED_STATE_ID: number = 3;
    REVERSED_STATE_ID: number = 4;
    DELETED_STATE_ID: number = 5;

    readonly STATE_TRANSITIONS: Record<number, { label: string; target: string }[]> = {
        1: [{ label: "Aceptar", target: "ACCEPTED" }, { label: "Eliminar", target: "DELETED" }],
        2: [{ label: "Procesar", target: "PROCESSED" }, { label: "Eliminar", target: "DELETED" }],
        3: [{ label: "Revertir", target: "REVERSED" }],
        4: [{ label: "Aceptar", target: "ACCEPTED" }],
    };

    async mounted() {
        await this.loadBatchesStates();
        await this.loadBatches(1, this.currentSort);
        this.loading = false;
    }

    isPending(stateId: number): boolean {
        return stateId === this.PENDING_STATE_ID;
    }

    isInProgress(stateId: number): boolean {
        return stateId === this.IN_PROGRESS_STATE_ID;
    }

    isAccepted(stateId: number): boolean {
        return stateId === this.ACCEPTED_STATE_ID;
    }

    isProcessed(stateId: number): boolean {
        return stateId === this.PROCESSED_STATE_ID;
    }

    isReversed(stateId: number): boolean {
        return stateId === this.REVERSED_STATE_ID;
    }

    canShowActions(stateId: number): boolean {
        return this.isPending(stateId) || this.isAccepted(stateId)
            || this.isProcessed(stateId) || this.isReversed(stateId);
    }

    canShowBordereaux(stateId: number): boolean {
        return this.isPending(stateId) || this.isAccepted(stateId) || this.isProcessed(stateId);
    }

    canShowDetailsAction(stateId: number): boolean {
        return this.isPending(stateId) || this.isAccepted(stateId) || this.isProcessed(stateId);
    }

    get stateConfirmText(): string {
        if (!this.pendingStateChange) {
            return "¿Está seguro que desea cambiar el estado de este batch?";
        }

        const baseText = `¿Está seguro que desea ${this.pendingStateChange.label.toLowerCase()} este batch?`;
        const willLoseEdit = this.pendingStateChange.sourceStateId === this.PENDING_STATE_ID
            && this.pendingStateChange.targetState !== "DELETED";

        if (!willLoseEdit) {
            return baseText;
        }

        return `${baseText}<br/><br/>Luego de este cambio, no se podrá editar este batch.`;
    }

    get stateConfirmButtonText(): string {
        if (!this.pendingStateChange) {
            return "Confirmar";
        }

        return this.pendingStateChange.targetState === "DELETED"
            ? "Eliminar"
            : this.pendingStateChange.label;
    }

    stateClass(stateId: number): string {
        if (stateId === this.PENDING_STATE_ID) return "pending";
        if (stateId === this.IN_PROGRESS_STATE_ID) return "in-progress";
        if (stateId === this.REVERSED_STATE_ID || stateId === this.DELETED_STATE_ID) return "inactive";
        if (stateId === this.ERROR_STATE_ID) return "inactive";
        return "active";
    }

    clearFilters() {
        this.filter = {
            code: "",
            requestNumber: "",
            claimNumber: "",
            stateId: ""
        };
        this.loadBatches(1, this.currentSort);
    }

    onPageChange(page: number, limit: number) {
        this.limit = limit;
        this.loadBatches(page, this.currentSort);
    }

    async loadBatches(page: number, sort: Record<string, string>) {
        this.currentSort = sort;
        try {
            const response: any = await (this.$root as any)._get("batches", {
                params: {
                    limit: this.limit,
                    page,
                    order: this.currentSort,
                    ...this.filter
                }
            });

            this.batches = response.data || response;
            this.totalRows = response.totalRows !== undefined && response.totalRows !== null ? response.totalRows : this.batches.length;
        } catch (e) {
            (this.$root as any).notify("No se pudieron cargar los batches.", "error");
        }
    }

    async loadBatchesStates() {
        try {
            const response: any = await (this.$root as any)._get("batch-states");
            this.batchesStates = response.data || response;
        } catch (e) {
            (this.$root as any).notify("No se pudieron cargar los estados de batch.", "error");
        }
    }

    async changeState(batchId: number, targetState: string) {
        await (this.$root as any)._patch(`batches/${batchId}/state`, { state: targetState });
        await this.loadBatches(1, this.currentSort);
    }

    openStateConfirm(batchId: number, targetState: string, label: string, sourceStateId: number) {
        this.pendingStateChange = { batchId, targetState, label, sourceStateId };
        (this.$refs.confirmStateChange as any).show();
    }

    cancelStateConfirm(modal: any) {
        this.pendingStateChange = null;
        modal.hide();
    }

    async onConfirmStateChange(modal: any) {
        modal.hide();
        if (this.pendingStateChange) {
            await this.changeState(this.pendingStateChange.batchId, this.pendingStateChange.targetState);
            this.pendingStateChange = null;
        }
    }

    openBordereauxConfirm(batchId: number) {
        this.pendingBatchId = batchId;
        this.bordereauxOptions = { sendEmail: false, emailTo: (this.$store as any).state.email || "" };
        (this.$refs.confirmBordereaux as any).show();
    }

    cancelBordereauxConfirm(modal: any) {
        this.pendingBatchId = null;
        modal.hide();
    }

    async onConfirmBordereaux(modal: any) {
        modal.hide();
        if (this.pendingBatchId !== null) {
            const batchId = this.pendingBatchId;
            this.pendingBatchId = null;
            await this.generateBordereaux(batchId);
        }
    }

    async generateBordereaux(batchId: number) {
        try {
            (this.$refs.processingBordereaux as any).show();

            const authToken = await (this.$auth as any).getAccessToken();
            const dataToken = (this.$store as any).state.token;
            const headers: Record<string, string> = {
                Authorization: `Bearer ${authToken}`,
                "Ocp-Apim-Subscription-Key": process.env.VUE_APP_AZURE_APIM_ID || ""
            };

            if (dataToken) {
                headers["x-data-token"] = dataToken;
            }

            const params: Record<string, any> = {
                sendEmail: this.bordereauxOptions.sendEmail
            };
            const body: Record<string, any> = {};

            if (this.bordereauxOptions.sendEmail && this.bordereauxOptions.emailTo) {
                body.emailTo = this.bordereauxOptions.emailTo;
            }

            const response = await axios.post(
                `${process.env.VUE_APP_API_URL}batches/${batchId}/bordereaux`,
                body,
                {
                    headers,
                    params
                }
            );

            const { fileName, data } = response.data;
            const binaryString = atob(data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: 'application/zip' });
            this.downloadFile(blob, fileName);

            (this.$refs.processingBordereaux as any).hide();
            (this.$refs.successBordereaux as any).show();
        } catch (e) {
            (this.$refs.processingBordereaux as any).hide();
            (this.$root as any).notify(
                "No se pudo generar el bordereaux.",
                "error"
            );
        }
    }

    downloadFile(fileData: BlobPart, fileName: string) {
        const url = window.URL.createObjectURL(new Blob([fileData]));
        const link = document.createElement("a");

        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
    }

    getFileNameFromResponseHeaders(headers: Record<string, any>, batchId: number): string {
        const contentDisposition = this.getHeaderValue(headers, "content-disposition");
        const headerFileName = this.getHeaderValue(headers, "x-file-name") || this.getHeaderValue(headers, "x-filename");

        return this.getFileNameFromDisposition(contentDisposition) || headerFileName || `batch_${batchId}.zip`;
    }

    getHeaderValue(headers: Record<string, any>, name: string): string {
        if (!headers) {
            return "";
        }

        const directValue = headers[name] || headers[name.toLowerCase()] || headers[name.toUpperCase()];
        if (directValue) {
            return String(directValue);
        }

        const matchingKey = Object.keys(headers).find((key) => key.toLowerCase() === name.toLowerCase());
        return matchingKey ? String(headers[matchingKey]) : "";
    }

    getFileNameFromDisposition(contentDisposition: string): string | null {
        if (!contentDisposition) {
            return null;
        }

        const utf8FileNameMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
        if (utf8FileNameMatch && utf8FileNameMatch[1]) {
            try {
                return decodeURIComponent(utf8FileNameMatch[1].replace(/^\"|\"$/g, "").trim());
            } catch (e) {
                return utf8FileNameMatch[1].replace(/^\"|\"$/g, "").trim();
            }
        }

        const fileNameMatch = contentDisposition.match(/filename\s*=\s*\"?([^\";]+)\"?/i);
        return fileNameMatch && fileNameMatch[1] ? fileNameMatch[1].trim() : null;
    }
}
